// chat-widget.js
// Vanilla JS — no dependencies, no build step.
// Drops into any Shopify theme without conflicts.
//
// To install on a Shopify store:
// 1. Upload this file to the store's Assets folder
// 2. Add the following to theme.liquid just before </body>:
//
//    <script>
//      window.CHAT_CONFIG = {
//        storeId: "sixty61",
//        apiBase: "https://YOUR-CLOUD-RUN-URL",
//        agentName: "Alex",
//        primaryColor: "#1a1a2e",
//        useAppProxy: true,  // set to true if using the Shopify app proxy
//      };
//    </script>
//    <script src="{{ 'chat-widget.js' | asset_url }}" defer></script>
//
// Language and UI labels come from the server — no per-language config needed.
//
// storeId must match one of: sixty61, gpr_direct, gpr_systems, magma
// agentName is displayed in the widget header
// primaryColor controls the header and send button color

(function () {
  "use strict";

  // ---------------------------------------------------------------------------
  // Config — read from window.CHAT_CONFIG set in theme.liquid
  // Defaults are safe fallbacks if config is missing
  // ---------------------------------------------------------------------------
  const config = window.CHAT_CONFIG || {};

  const CONFIG_WAS_MISSING = !config.storeId;

  const STORE_ID     = config.storeId     || "sixty61";
  const API_BASE     = config.apiBase     || "";
  const AGENT_NAME   = config.agentName   || "Support";
  const PRIMARY_COLOR = config.primaryColor || "#1a1a2e";
  const USE_APP_PROXY = config.useAppProxy === true;
  
  if (CONFIG_WAS_MISSING) {
    console.warn("CHAT_CONFIG.storeId is missing — defaulting to 'sixty61'. Please set window.CHAT_CONFIG in theme.liquid.");
  }

  // ---------------------------------------------------------------------------
  // State
  // Kept in module scope — persists for the page lifetime.
  // sessionStorage is used for session_id so it survives page navigation
  // within the same tab but resets when the tab is closed.
  // ---------------------------------------------------------------------------
  let sessionId = sessionStorage.getItem("chat_session_" + STORE_ID) || null;
  let sessionStarted = false;
  let isOpen = false;
  let isWaiting = false; // True while waiting for AI response — blocks double sends

  // ---------------------------------------------------------------------------
  // Locale (spec 2026-10-07 §9.1)
  // The server owns all chat text and sends this session's language + UI
  // labels in the /start response. We keep them in sessionStorage so the
  // labels stay translated if the widget is rebuilt on another page.
  // ---------------------------------------------------------------------------
  const UI_KEY = "chat_ui_" + STORE_ID;

  function loadSavedUi() {
    try {
      return JSON.parse(sessionStorage.getItem(UI_KEY) || "null");
    } catch (e) {
      return null;
    }
  }

  let savedUi = loadSavedUi();

  // Shown only when the server can't answer, so these can't come from it.
  // The ONLY strings that need a re-paste when a language is added.
  const BUILTIN_TEXT = {
    en: {
      connection: "Having trouble connecting — please refresh and try again.",
      slow_down:  "Please wait a moment before sending another message.",
      error:      "Something went wrong — please close and reopen the chat to continue.",
    },
    es: {
      connection: "Hay problemas de conexión — por favor actualiza la página e inténtalo de nuevo.",
      slow_down:  "Por favor espera un momento antes de enviar otro mensaje.",
      error:      "Algo salió mal — por favor cierra y vuelve a abrir el chat para continuar.",
    },
    fr: {
      connection: "Problème de connexion — veuillez actualiser la page et réessayer.",
      slow_down:  "Veuillez patienter un instant avant d'envoyer un autre message.",
      error:      "Une erreur s'est produite — veuillez fermer puis rouvrir la discussion pour continuer.",
    },
    "fr-CA": {
      connection: "Problème de connexion — veuillez actualiser la page et réessayer.",
      slow_down:  "Veuillez patienter un instant avant d'envoyer un autre message.",
      error:      "Une erreur s'est produite — veuillez fermer puis rouvrir la discussion pour continuer.",
    },
    it: {
      connection: "Problemi di connessione — aggiorna la pagina e riprova.",
      slow_down:  "Attendi un momento prima di inviare un altro messaggio.",
      error:      "Qualcosa è andato storto — chiudi e riapri la chat per continuare.",
    },
  };

  function currentLanguage() {
    if (savedUi && savedUi.language) return savedUi.language;
    return (navigator.language || "en").slice(0, 2).toLowerCase();
  }

  function builtinText(key) {
    // Regional codes (fr-CA) fall back to their base language, then English.
    const language = currentLanguage();
    const table = BUILTIN_TEXT[language] || BUILTIN_TEXT[language.split("-")[0]] || BUILTIN_TEXT.en;
    return table[key];
  }

  function applyUiText(uiText) {
    if (!uiText) return;
    const sub = document.getElementById("chat-header-sub");
    const disclaimer = document.getElementById("chat-disclaimer");
    if (sub && uiText.header_subtitle) sub.textContent = uiText.header_subtitle;
    if (disclaimer && uiText.disclaimer) disclaimer.textContent = uiText.disclaimer;
    if (inputEl && uiText.placeholder) inputEl.placeholder = uiText.placeholder;
  }

  // ---------------------------------------------------------------------------
  // Build the widget DOM
  // We inject all HTML and CSS from JS so the widget has zero theme dependencies.
  // ---------------------------------------------------------------------------

  function buildStyles() {
    const style = document.createElement("style");
    style.textContent = `
      #chat-launcher {
        position: fixed;
        bottom: 24px;
        right: 24px;
        width: 56px;
        height: 56px;
        border-radius: 50%;
        background: ${PRIMARY_COLOR};
        border: none;
        cursor: pointer;
        box-shadow: 0 4px 12px rgba(0,0,0,0.25);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 9998;
        transition: transform 0.2s ease;
      }
      #chat-launcher:hover { transform: scale(1.08); }
      #chat-launcher svg { width: 26px; height: 26px; fill: white; }

      #chat-widget {
        position: fixed;
        bottom: 92px;
        right: 24px;
        width: 360px;
        max-height: 520px;
        background: #fff;
        border-radius: 16px;
        box-shadow: 0 8px 32px rgba(0,0,0,0.18);
        display: flex;
        flex-direction: column;
        z-index: 9999;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        font-size: 14px;
        overflow: hidden;
        transform: scale(0.95) translateY(8px);
        opacity: 0;
        pointer-events: none;
        transition: opacity 0.2s ease, transform 0.2s ease;
      }
      #chat-widget.open {
        opacity: 1;
        transform: scale(1) translateY(0);
        pointer-events: all;
      }

      #chat-header {
        background: ${PRIMARY_COLOR};
        color: white;
        padding: 14px 16px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        border-radius: 16px 16px 0 0;
      }
      #chat-header-title { font-weight: 600; font-size: 15px; }
      #chat-header-sub {
        font-size: 11px;
        opacity: 0.75;
        margin-top: 1px;
      }
      #chat-close-btn {
        background: none;
        border: none;
        color: white;
        cursor: pointer;
        font-size: 20px;
        padding: 0 4px;
        line-height: 1;
        opacity: 0.8;
      }
      #chat-close-btn:hover { opacity: 1; }

      #chat-disclaimer {
        background: #f8f8f8;
        border-bottom: 1px solid #eee;
        padding: 6px 14px;
        font-size: 11px;
        color: #888;
        text-align: center;
      }

      #chat-messages {
        flex: 1;
        overflow-y: auto;
        padding: 14px;
        display: flex;
        flex-direction: column;
        gap: 10px;
        min-height: 200px;
      }

      .chat-bubble {
        max-width: 82%;
        padding: 10px 13px;
        border-radius: 12px;
        line-height: 1.45;
        word-break: break-word;
      }
      .chat-bubble.ai {
        background: #f1f1f1;
        color: #1a1a1a;
        align-self: flex-start;
        border-bottom-left-radius: 3px;
      }
      .chat-bubble.customer {
        background: ${PRIMARY_COLOR};
        color: white;
        align-self: flex-end;
        border-bottom-right-radius: 3px;
      }
      .chat-bubble.ai a {
        color: ${PRIMARY_COLOR};
        text-decoration: underline;
        word-break: break-all;
      }

      .typing-indicator {
        background: #f1f1f1;
        align-self: flex-start;
        padding: 12px 16px;
        border-radius: 12px;
        border-bottom-left-radius: 3px;
        display: flex;
        gap: 4px;
        align-items: center;
      }
      .typing-dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: #aaa;
        animation: typingBounce 1.2s infinite;
      }
      .typing-dot:nth-child(2) { animation-delay: 0.2s; }
      .typing-dot:nth-child(3) { animation-delay: 0.4s; }
      @keyframes typingBounce {
        0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
        40% { transform: translateY(-5px); opacity: 1; }
      }

      #chat-quick-replies {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        padding: 0 14px 10px;
      }
      .quick-reply-btn {
        background: white;
        border: 1.5px solid ${PRIMARY_COLOR};
        color: ${PRIMARY_COLOR};
        padding: 6px 11px;
        border-radius: 20px;
        cursor: pointer;
        font-size: 12px;
        font-weight: 500;
        transition: background 0.15s, color 0.15s;
        white-space: nowrap;
      }
      .quick-reply-btn:hover {
        background: ${PRIMARY_COLOR};
        color: white;
      }

      #chat-input-row {
        display: flex;
        align-items: center;
        padding: 10px 12px;
        border-top: 1px solid #eee;
        gap: 8px;
      }
      #chat-input {
        flex: 1;
        border: 1.5px solid #e0e0e0;
        border-radius: 20px;
        padding: 9px 14px;
        font-size: 14px;
        outline: none;
        resize: none;
        font-family: inherit;
        max-height: 80px;
        overflow-y: auto;
        line-height: 1.4;
        transition: border-color 0.15s;
      }
      #chat-input:focus { border-color: ${PRIMARY_COLOR}; }
      #chat-send-btn {
        width: 38px;
        height: 38px;
        border-radius: 50%;
        background: ${PRIMARY_COLOR};
        border: none;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        transition: opacity 0.15s;
      }
      #chat-send-btn:disabled { opacity: 0.4; cursor: not-allowed; }
      #chat-send-btn svg { width: 16px; height: 16px; fill: white; }

      @media (max-width: 400px) {
        #chat-widget {
          width: calc(100vw - 16px);
          right: 8px;
          bottom: 80px;
        }
      }
    `;
    document.head.appendChild(style);
  }

  function buildHTML() {
    // Launcher button
    const launcher = document.createElement("button");
    launcher.id = "chat-launcher";
    launcher.setAttribute("aria-label", "Open chat");
    launcher.innerHTML = `
      <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
      </svg>`;
    document.body.appendChild(launcher);

    // Widget panel
    const widget = document.createElement("div");
    widget.id = "chat-widget";
    widget.setAttribute("role", "dialog");
    widget.setAttribute("aria-label", "Customer support chat");
    widget.innerHTML = `
      <div id="chat-header">
        <div>
          <div id="chat-header-title">${AGENT_NAME}</div>
          <div id="chat-header-sub">Typically replies in seconds</div>
        </div>
        <button id="chat-close-btn" aria-label="Close chat">&#x2715;</button>
      </div>
      <div id="chat-disclaimer">
        AI-powered assistant — for complex issues our team will follow up
      </div>
      <div id="chat-messages" role="log" aria-live="polite"></div>
      <div id="chat-quick-replies"></div>
      <div id="chat-input-row">
        <textarea
          id="chat-input"
          placeholder="Type a message..."
          rows="1"
          aria-label="Type your message"
          maxlength="1000"
        ></textarea>
        <button id="chat-send-btn" aria-label="Send message">
          <svg viewBox="0 0 24 24"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>
        </button>
      </div>`;
    document.body.appendChild(widget);
  }

  // ---------------------------------------------------------------------------
  // DOM references — populated after buildHTML() runs
  // ---------------------------------------------------------------------------
  let widgetEl, messagesEl, inputEl, sendBtn, quickRepliesEl, launcherEl;

  function bindElements() {
    widgetEl      = document.getElementById("chat-widget");
    messagesEl    = document.getElementById("chat-messages");
    inputEl       = document.getElementById("chat-input");
    sendBtn       = document.getElementById("chat-send-btn");
    quickRepliesEl = document.getElementById("chat-quick-replies");
    launcherEl    = document.getElementById("chat-launcher");
  }

  // ---------------------------------------------------------------------------
  // UI helpers
  // ---------------------------------------------------------------------------

  function appendMessage(role, text) {
    // Clear quick replies whenever a new message appears
    quickRepliesEl.innerHTML = "";

    const bubble = document.createElement("div");
    bubble.className = `chat-bubble ${role}`;
    if (role === "ai") {
      appendLinkedText(bubble, text);
    } else {
      bubble.textContent = text;
    }
    messagesEl.appendChild(bubble);
    scrollToBottom();
    return bubble;
  }

  // Only https links to THIS store's own site become clickable (e.g. the
  // Contact Us page in the give-up reply). Any other URL — including one
  // the AI made up — stays plain text, so a reply can never put a
  // clickable link to another site in front of a customer. Built from
  // text nodes and <a> elements, never innerHTML, so reply text can't
  // inject markup.
  function isOwnSiteUrl(url) {
    try {
      const parsed = new URL(url);
      const bareHost = (host) => host.toLowerCase().replace(/^www\./, "");
      return parsed.protocol === "https:" && bareHost(parsed.hostname) === bareHost(window.location.hostname);
    } catch (e) {
      return false;
    }
  }

  function appendLinkedText(el, text) {
    const urlPattern = /https:\/\/[^\s<>"]+/g;
    let lastIndex = 0;
    let match;
    while ((match = urlPattern.exec(text)) !== null) {
      // A sentence's closing punctuation isn't part of the link.
      const url = match[0].replace(/[.,!?;:)]+$/, "");
      el.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
      if (isOwnSiteUrl(url)) {
        const link = document.createElement("a");
        link.href = url;
        link.textContent = url;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        el.appendChild(link);
      } else {
        el.appendChild(document.createTextNode(url));
      }
      lastIndex = match.index + url.length;
    }
    el.appendChild(document.createTextNode(text.slice(lastIndex)));
  }

  function showTypingIndicator() {
    const el = document.createElement("div");
    el.className = "typing-indicator";
    el.id = "typing-indicator";
    el.innerHTML = `
      <div class="typing-dot"></div>
      <div class="typing-dot"></div>
      <div class="typing-dot"></div>`;
    messagesEl.appendChild(el);
    scrollToBottom();
  }

  function removeTypingIndicator() {
    const el = document.getElementById("typing-indicator");
    if (el) el.remove();
  }

  function renderQuickReplies(replies) {
    quickRepliesEl.innerHTML = "";
    if (!replies || replies.length === 0) return;

    replies.forEach(function (label) {
      const btn = document.createElement("button");
      btn.className = "quick-reply-btn";
      btn.textContent = label;
      btn.addEventListener("click", function () {
        // Treat a quick reply tap exactly like typing and sending that message
        quickRepliesEl.innerHTML = "";
        sendMessage(label);
      });
      quickRepliesEl.appendChild(btn);
    });
  }

  function scrollToBottom() {
    messagesEl.scrollTop = messagesEl.scrollHeight;
  }

  function setWaiting(waiting) {
    isWaiting = waiting;
    sendBtn.disabled = waiting;
    inputEl.disabled = waiting;
  }

  // ---------------------------------------------------------------------------
  // API calls
  // ---------------------------------------------------------------------------

  async function startSession() {
    // If we already have a session_id in sessionStorage, reuse it.
    // This handles page navigation within the same tab — the customer
    // doesn't lose their conversation if they click to a different page.
    if (sessionId) {
      sessionStarted = true;
      return;
    }

    try {
      const startUrl = USE_APP_PROXY
        ? "/apps/ai-chat/start"
        : (API_BASE + "/api/v1/chat/start");

      // Spec §3.1 / §6.1 — Shopify sets window.Shopify.country from its own
      // visitor geolocation; navigator.language is the browser setting.
      const localeSignals = {
        shopify_country:  (window.Shopify && window.Shopify.country) || "",
        browser_language: navigator.language || "",
      };

      const body = USE_APP_PROXY
        ? Object.assign({ config_was_missing: CONFIG_WAS_MISSING, page_url: window.location.href }, localeSignals)   // no email/name — proxy supplies identity
        : Object.assign({
            store_id: STORE_ID,
            email:   config.customerEmail || "",
            name:    config.customerName  || "",
            config_was_missing: CONFIG_WAS_MISSING,
            page_url: window.location.href,
          }, localeSignals);

      const res = await fetch(startUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!res.ok) throw new Error("Start failed: " + res.status);

      const data = await res.json();
      sessionId = data.session_id;

      // Persist in sessionStorage so page navigation doesn't lose the session
      sessionStorage.setItem("chat_session_" + STORE_ID, sessionId);
      sessionStarted = true;

      // Server-supplied language + UI text (old servers send neither — keep English).
      if (data.ui_text) {
        savedUi = { language: data.language || "en", ui_text: data.ui_text };
        try {
          sessionStorage.setItem(UI_KEY, JSON.stringify(savedUi));
        } catch (e) { /* storage full or blocked — labels just won't persist */ }
        applyUiText(data.ui_text);
      }

      // Show the opening message from the agent
      appendMessage(
        "ai",
        (data.ui_text && data.ui_text.greeting) ||
          ("Hi! I'm " + AGENT_NAME + ", your " + STORE_ID.replace(/_/g, " ") +
           " assistant. How can I help you today?")
      );

    } catch (err) {
      console.error("Chat session start error:", err);
      appendMessage("ai", builtinText("connection"));
    }
  }

  async function sendMessage(text) {
    const trimmed = (text || inputEl.value).trim();
    if (!trimmed || isWaiting) return;

    // Clear the input immediately
    inputEl.value = "";
    inputEl.style.height = "auto";

    // Show the customer's message in the UI right away — don't wait for the API
    appendMessage("customer", trimmed);
    setWaiting(true);
    showTypingIndicator();

    try {
      const messageUrl = USE_APP_PROXY
        ? "/apps/ai-chat/message"
        : (API_BASE + "/api/v1/chat/message");

      const res = await fetch(messageUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId,
          store_id:   STORE_ID,
          message:    trimmed,
          page_url:   window.location.href,
        }),
      });

      removeTypingIndicator();

      if (!res.ok) {
        // Rate limit hit
        if (res.status === 429) {
          appendMessage("ai", builtinText("slow_down"));
          setWaiting(false);
          return;
        }
        throw new Error("Message failed: " + res.status);
      }

      const data = await res.json();
      appendMessage("ai", data.reply);
      renderQuickReplies(data.quick_replies);

    } catch (err) {
      removeTypingIndicator();
      console.error("Chat message error:", err);
      
      // If we get a 500, the session may be unrecoverable client-side.
      // Clear it so the next open starts a fresh session.
      if (err.message && err.message.includes("500")) {
        sessionStorage.removeItem("chat_session_" + STORE_ID);
        sessionId = null;
        sessionStarted = false;
      }
      
      appendMessage("ai", builtinText("error"));
    }

    setWaiting(false);
  }

  // ---------------------------------------------------------------------------
  // Open / close
  // ---------------------------------------------------------------------------

  function openWidget() {
    if (isOpen) return;
    isOpen = true;
    widgetEl.classList.add("open");
    launcherEl.setAttribute("aria-expanded", "true");

    if (!sessionStarted) {
      inputEl.disabled = true;
      sendBtn.disabled = true;
      startSession().finally(() => {
        inputEl.disabled = false;
        sendBtn.disabled = false;
        inputEl.focus();
      });
    } else {
      inputEl.focus();
    }
  }

  function _sendEndSession() {
    // Sends /chat/end to the backend.
    // Called from three places:
    //   1. closeWidget() — customer clicks the X button
    //   2. beforeunload  — tab closes or page navigates away
    //   3. visibilitychange — page hidden (mobile background, tab switch)
    //
    // WHY keepalive: true ON ALL CALLS:
    // keepalive allows the fetch to complete even after the page is unloading.
    // Without it, the browser cancels in-flight requests when navigating away.
    // This is the only reliable way to fire /chat/end on tab close.
    //
    // WHY WE GUARD WITH sessionStarted:
    // If the widget was opened but the session never started (network error
    // on /chat/start), there is nothing to end. Avoid a wasted API call.
    //
    // WHY WE CLEAR sessionId AFTER SENDING:
    // Prevents duplicate /chat/end calls if the function is called twice
    // (e.g. closeWidget fires AND beforeunload fires in quick succession).
 
    if (!sessionId || !sessionStarted) return;
 
    var currentSessionId = sessionId;
    sessionId = null;          // Clear immediately to prevent duplicate calls
    sessionStarted = false;
    sessionStorage.removeItem("chat_session_" + STORE_ID);
 
    // Send the end session request
    const endUrl = USE_APP_PROXY
      ? "/apps/ai-chat/end"
      : (API_BASE + "/api/v1/chat/end");

    fetch(endUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        session_id: currentSessionId,
        store_id:   STORE_ID,
        page_url:   window.location.href,
      }),
      keepalive: true,
    }).catch(function () {});
  }  

  function closeWidget() {
    if (!isOpen) return;
    isOpen = false;
    widgetEl.classList.remove("open");
    launcherEl.setAttribute("aria-expanded", "false");
  }

  // ---------------------------------------------------------------------------
  // Event listeners
  // ---------------------------------------------------------------------------

  function bindEvents() {
    // Launcher click — open the widget
    launcherEl.addEventListener("click", openWidget);

    // Close button
    document.getElementById("chat-close-btn").addEventListener("click", closeWidget);

    // Send button click
    sendBtn.addEventListener("click", function () {
      sendMessage();
    });

    // Enter key sends, Shift+Enter adds a newline
    inputEl.addEventListener("keydown", function (e) {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
      }
    });

    // Auto-resize textarea as customer types
    inputEl.addEventListener("input", function () {
      this.style.height = "auto";
      this.style.height = Math.min(this.scrollHeight, 80) + "px";
    });

    // Close on Escape key
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && isOpen) closeWidget();
    });

    // Fire /chat/end when the tab closes or the page navigates away.
    // This is the primary fix for sessions that never get finalized
    // because the customer closes the browser without clicking X.
    // keepalive: true in _sendEndSession() ensures the request completes
    // even during page unload.
    window.addEventListener("beforeunload", function () {
      _sendEndSession();
    });
 
    // Secondary safety net for mobile browsers.
    // On mobile, tabs are often backgrounded rather than closed.
    // visibilitychange fires when the page is hidden — we treat this
    // as a session end signal since the customer has left the chat.
    // We only fire if the widget was open (isOpen check) to avoid
    // ending sessions when the customer just switches tabs briefly.
    document.addEventListener("visibilitychange", function () {
      if (document.visibilityState === "hidden" && isOpen) {
        // NOTE (2026-07-25 fix): previously called _sendEndSession() here.
        // Tab-hide fires on ANY tab switch or app-switch, not just when the
        // customer actually leaves — it was finalizing + logging the session
        // to ReAmaze and clearing sessionId, so returning to the tab started
        // a brand-new session. That fragmented single visits into multiple
        // near-empty ReAmaze tickets, and could orphan a session mid-escalation
        // (see Patch 2). beforeunload (real navigation/close) and the explicit
        // X button remain the real end signals. True abandonment is still
        // caught by cleanup_job's 30-minute sweep, so no coverage is lost.
        isOpen = false;
        if (widgetEl) widgetEl.classList.remove("open");
        if (launcherEl) launcherEl.setAttribute("aria-expanded", "false");
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Init — runs when the script loads (deferred, so DOM is ready)
  // ---------------------------------------------------------------------------

  function init() {
    buildStyles();
    buildHTML();
    bindElements();
    applyUiText(savedUi && savedUi.ui_text);
    bindEvents();
  }

  init();

})();